import { yupResolver } from '@hookform/resolvers/yup';
import {
	Box,
	Button,
	FormControl,
	FormHelperText,
	Grid,
	InputAdornment,
	TextField,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import {
	activeUserAccount,
	loginUser,
	sentVerificationEmail,
} from 'store/user-service/asyncActions';
import { getUser } from 'store/user-service/selector';
import { removeLoginError } from 'store/user-service/userSlice';

import { ROUTES } from 'core/constants/routes';
import { NamespacesList } from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';
import { loginSchema } from 'core/helpers/yupSchemas';
import { SignInType } from 'core/types/base';

import { ErrorVerifyEmailModal } from 'pages/SignUp/ErrorVerifyEmailModal';

import { LoginHeader } from 'components/Header/LoginHeader';

import { ReactComponent as EyeSlash } from 'assets/icons/eye-slash.svg';

import { SendVerifyLetterEmailModal } from './SendVerifyLetterModal';

export const Login = () => {
	const { t: logInPageTranslate } = useTranslation(NamespacesList['logInPage']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);
	const { t: modalsTranslate } = useTranslation(NamespacesList['modals']);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const userData = useSelector(getUser);

	const [showPassword, setShowPassword] = useState(false);
	const [openActivatingErrorVerify, setIsOpenActivatingErrorVerify] = useState(false);
	const [openSendingErrorVerify, setIsOpenSendingErrorVerify] = useState(false);
	const [openSendingVerify, setIsOpenSendingVerify] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const [userEmail, setUserEmail] = useState('');

	const {
		handleSubmit,
		register,
		formState: { errors },
		watch,
		getValues,
		reset,
	} = useForm({
		resolver: yupResolver(loginSchema()),
	});

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleActionActivatingError = () => {
		setIsOpenActivatingErrorVerify((prev) => !prev);
	};

	const handleActionSendingError = () => {
		setIsOpenSendingErrorVerify((prev) => !prev);
	};

	const handleActionSendingVerify = () => {
		setIsOpenSendingVerify((prev) => !prev);
	};

	const handleSentVerifyEmail = async () => {
		const emailToVerify = getValues().email || userEmail;
		if (emailToVerify) {
			!userEmail && setUserEmail(emailToVerify);
			await dispatch(
				sentVerificationEmail({
					redirectLink: window.location.href,
					userEmail: emailToVerify,
					successFunction: !openSendingVerify ? handleActionSendingVerify : undefined,
					errorFunction: handleActionSendingError,
				})
			);
			reset();
		}
	};

	const activateYourAccountMessage = 'You need to activate your account';
	const incorrectLoginDataMessage = 'Login with this credentials was failed';

	const handleLoginError = async (error: any) => {
		if (error.status === 403 && error.data === activateYourAccountMessage) {
			await handleSentVerifyEmail();
			errorNotify(
				modalsTranslate('isActivatedAccountError.title'),
				modalsTranslate('isActivatedAccountError.description')
			);
		}

		if (error.status === 400 && error.data === incorrectLoginDataMessage) {
			errorNotify(
				modalsTranslate('incorrectLoginDataError.title'),
				modalsTranslate('incorrectLoginDataError.description')
			);
		}

		if (error.data !== activateYourAccountMessage && error.data !== incorrectLoginDataMessage) {
			errorNotify(commonTranslate('errors.error'), error.data);
		}

		return error;
	};

	const handleLogin = async (data: SignInType['userData']) => {
		try {
			const result = await dispatch(
				loginUser({
					userData: data,
				})
			).unwrap();

			if (result.status === 200) {
				navigate(ROUTES.HOME);
			} else {
				await handleLoginError(result);
				dispatch(removeLoginError());
			}
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		dispatch(removeLoginError());
	}, [watch('email'), watch('password')]);

	const redirectToSignUp = () => {
		navigate(ROUTES.SIGNUP);
	};

	const redirectToResetPassword = () => {
		navigate(ROUTES.RESET_PASSWORD);
	};

	const renderEmailInfo = () => {
		const emailError = 'User with email';

		if (userData.loginError?.startsWith(emailError)) {
			return (
				<FormHelperText
					sx={{
						fontSize: '12px',
						textAlign: 'right',
						marginRight: '12px',
						marginTop: '0',
						color: '#FB0B24',
					}}
				>
					{logInPageTranslate('errors.incorrectEmail')}
				</FormHelperText>
			);
		} else {
			return (
				<FormHelperText
					sx={{
						fontSize: '12px',
						textAlign: 'right',
						marginRight: '12px',
						marginTop: '0',
					}}
				>
					{errors.email?.message}
				</FormHelperText>
			);
		}
	};

	const renderPasswordInfo = () => {
		const passwordError = 'Login with this credentials was failed';

		if (userData.loginError === passwordError) {
			return (
				<FormHelperText
					sx={{
						fontSize: '12px',
						textAlign: 'right',
						marginRight: '12px',
						marginTop: '0',
						color: '#FB0B24',
					}}
				>
					{logInPageTranslate('errors.incorrectPassword')}
				</FormHelperText>
			);
		}
		if (errors.password) {
			return (
				<FormHelperText
					sx={{
						fontSize: '12px',
						textAlign: 'right',
						marginRight: '12px',
						marginTop: '0',
					}}
				>
					{errors.password?.message}
				</FormHelperText>
			);
		}

		return null;
	};

	const handleActiveAccount = () => {
		if (searchParams.get('email') && searchParams.get('token')) {
			dispatch(
				activeUserAccount({
					token: searchParams.get('token') || '',
					userEmail: searchParams.get('email') || '',
					toastifyTitle: modalsTranslate('successVerifyEmail.title'),
					toastifyDescription: modalsTranslate('successVerifyEmail.description'),
					successFunction: () => {
						searchParams.delete('token');
						searchParams.delete('email');
						setSearchParams(searchParams);
					},
					errorFunction: handleActionActivatingError,
				})
			);
		}
	};

	useEffect(() => {
		handleActiveAccount();
	}, []);

	return (
		<>
			<LoginHeader />

			<Box
				className="rtl-able"
				sx={{
					display: 'flex',
					justifyContent: 'center',
					height: 'calc(100vh - 164px)',
					alignItems: 'center',
				}}
			>
				<Grid container sx={{ width: '374px', gap: '24px' }}>
					<Grid item sx={{ width: '100%', textAlign: 'center' }}>
						<Typography variant="h3" sx={{ fontSize: '34px', fontWeight: '700' }}>
							{logInPageTranslate('text.logIn')}
						</Typography>
					</Grid>

					<Grid item>
						<form onSubmit={handleSubmit(handleLogin)}>
							<Grid container direction="column">
								<Grid item>
									<FormControl fullWidth error={!!errors.email}>
										<Typography sx={{ fontSize: '12px' }}>
											{logInPageTranslate('text.email')}
										</Typography>
										<TextField
											inputProps={{ style: { padding: '10px 12px' } }}
											placeholder="example@gmail.com"
											sx={{
												height: '44px',
												'& .MuiOutlinedInput-root': {
													borderRadius: '8px',
												},
											}}
											{...register('email')}
										/>
										{renderEmailInfo()}
									</FormControl>
								</Grid>

								<Grid item mt={2.4}>
									<FormControl fullWidth error={!!errors.password}>
										<Typography sx={{ fontSize: '12px' }}>
											{logInPageTranslate('text.password')}
										</Typography>
										<TextField
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<Button
															disableRipple
															sx={{
																minWidth: 0,
																'&:hover': {
																	backgroundColor: '#FFF',
																},
															}}
															onClick={handleClickShowPassword}
														>
															<EyeSlash />
														</Button>
													</InputAdornment>
												),
											}}
											inputProps={{
												style: {
													padding: '10px 12px',
												},
											}}
											sx={{
												height: '44px',
												'& .MuiOutlinedInput-root': {
													borderRadius: '8px',
												},
											}}
											autoComplete="password"
											type={showPassword ? 'text' : 'password'}
											placeholder={logInPageTranslate('placeholders.password') as string}
											{...register('password')}
										/>
										{renderPasswordInfo()}
										<Typography
											sx={{
												fontSize: '12px',
												textAlign: 'right',
												marginRight: '12px',
												color: '#1A5EEC',
												cursor: 'pointer',
											}}
											onClick={redirectToResetPassword}
										>
											{logInPageTranslate('buttons.forgotPassword')}
										</Typography>
									</FormControl>
								</Grid>
							</Grid>

							{userData.loading ? (
								<Button
									sx={{
										marginTop: '24px',
										height: '44px',
										width: '374px',
										borderRadius: '8px',
									}}
									type="submit"
									variant="contained"
									color="primary"
								>
									<Oval
										height={24}
										width={24}
										color="#fff"
										wrapperStyle={{}}
										wrapperClass=""
										visible={true}
										ariaLabel="oval-loading"
										secondaryColor="#91B6FF"
										strokeWidth={5}
										strokeWidthSecondary={5}
									/>
								</Button>
							) : (
								<Button
									sx={{
										marginTop: '24px',
										height: '44px',
										width: '374px',
										borderRadius: '8px',
										textTransform: 'none',
										fontSize: '16px',
										fontWeight: '700',
									}}
									type="submit"
									variant="contained"
									color="primary"
								>
									{commonTranslate('buttons.logIn')}
								</Button>
							)}
						</form>
					</Grid>

					<Grid item sx={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
						<Typography>{logInPageTranslate('text.doNotHaveAccount')}</Typography>
						<Typography sx={{ color: '#4786FF', cursor: 'pointer' }} onClick={redirectToSignUp}>
							{commonTranslate('buttons.signUp')}
						</Typography>
					</Grid>
				</Grid>
				<SendVerifyLetterEmailModal
					onClose={handleActionSendingVerify}
					isOpen={openSendingVerify}
					onConfirm={handleSentVerifyEmail}
				/>

				<ErrorVerifyEmailModal
					isOpen={openActivatingErrorVerify}
					onClose={handleActionActivatingError}
					onConfirm={() => {
						handleActiveAccount();
						handleActionActivatingError();
					}}
					description={modalsTranslate('errorVerifyEmail.activatingError') || ''}
				/>
				<ErrorVerifyEmailModal
					isOpen={openSendingErrorVerify}
					onConfirm={() => {
						handleSentVerifyEmail();
						handleActionSendingError();
					}}
					onClose={handleActionSendingError}
					description={modalsTranslate('errorVerifyEmail.sendingError') || ''}
				/>
			</Box>
		</>
	);
};
